import { CashAccount, Client, DirectTradingAccount, LegalAccountType, ManagedAccount, Trust } from '../graphql/schema';

export const US_STATES = {
  AA: 'Armed Forces America',
  AE: 'Armed Forces Europe',
  AL: 'Alabama',
  AK: 'Alaska',
  AP: 'Armed Forces Pacific',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  VI: 'Virgin Islands',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export const DEFAULT_RETIREMENT_AGE = 68;

export const DEFAULT_LIFE_EXPECTANCY = 90;

export const GOAL_MIN_TERM_YEARS = 1;
export const GOAL_MAX_TERM_YEARS = 50;

export const ACCOUNT_LIST: {
  label: string;
  value: string;
  limit?: number;
  errorText?: string;
  icon: string;
}[] = [
  {
    label: 'Traditional IRA',
    value: 'traditional_ira',
    limit: 1,
    errorText: 'You can only add one Traditional IRA per user.',
    icon: 'parasol',
  },
  {
    label: 'Roth IRA',
    value: 'roth_ira',
    limit: 1,
    errorText: 'You can only add one Roth IRA per user.',
    icon: 'parasol',
  },
  {
    label: 'SEP IRA',
    value: 'sep_ira',
    limit: 1,
    errorText: 'You can only add one SEP IRA per user.',
    icon: 'parasol',
  },
  {
    label: 'Individual taxable',
    value: 'individual_taxable',
    icon: 'plant',
  },
  {
    label: 'Cash Reserve',
    value: 'individual_cash_reserve',
    icon: 'dollar',
  },
  {
    label: 'Cash Reserve',
    value: 'cash_reserve',
    icon: 'dollar',
  },
];

export const SIMPLE_SOLO_401K_LIST: {
  label: string;
  value: string;
  limit?: number;
  errorText?: string;
  icon: string;
}[] = [
  {
    label: 'Traditional and Roth Solo 401(k)',
    value: 'solo_401k',
    limit: 1,
    errorText: 'Only one pair of Solo 401(k) plans can be added per household.',
    icon: 'parasol',
  },
];

export const JOINT_ACCOUNT_LIST: {
  label: string;
  value: string;
  limit?: number;
  errorText?: string;
  icon: string;
}[] = [
  {
    label: 'Joint Cash Reserve',
    value: 'joint_cash_reserve',
    icon: 'dollar',
  },
  {
    label: 'Joint taxable',
    value: 'joint_taxable',
    icon: 'dollar',
  },
];

export const TAXABLE_ACCOUNT_TYPES = ['individual_taxable', 'trust_taxable', 'joint_taxable'];
export const TAX_DEFERRED_ACCOUNT_TYPES = ['traditional_ira', 'roth_ira', 'sep_ira', 'solo_401k'];
export const AUTOGLIDE_ELIGIBLE_PURPOSES = [
  'retirement_income',
  'retirement_growth',
  'build_wealth',
  'house',
  'education',
  'other',
];

export const CASH_PURPOSES = [
  { value: 'education', label: 'Education' },
  { value: 'emergency_2', label: 'Emergency fund' },
  { value: 'house', label: 'House' },
  { value: 'other', label: 'Major purchase' },
];

export const TAX_ADVANTAGED_PURPOSES = [
  { value: 'retirement_growth', label: 'Retirement' },
  { value: 'retirement_income', label: 'Retirement income' },
];

export const TAXABLE_PURPOSES = CASH_PURPOSES.concat(TAX_ADVANTAGED_PURPOSES);

export const BUSINESS_TAX_CLASSIFICATIONS = {
  sole_proprietorship: 'Individual/sole proprietor or single-member LLC',
  c_corp: 'C Corporation',
  s_corp: 'S Corporation',
  partnership: 'Partnership',
  llc: 'Limited liability company',
  llp: 'Limited liability partnership',
  nonprofit: 'Non profit',
};

export const BENEFICIARY_DESIGNATION_DISCLOSURE_URL =
  'https://www.betterment.com/legal/beneficiary-designation-disclosure';

export const BENEFICIARY_DESIGNATION_FORM_SPOUSAL_CONSENT_FORM_URL =
  'https://s3.amazonaws.com/betterment-prod-cdn/forms/401k_beneficiary_designation_spousal_consent_form.pdf';

export const INVALID_EIN_PREFIXES = [
  '00',
  '07',
  '08',
  '09',
  '17',
  '18',
  '19',
  '28',
  '29',
  '49',
  '69',
  '70',
  '78',
  '79',
  '89',
  '96',
  '97',
];

export const CLIENT_TYPE_NAME: Client['__typename'] = 'Client';
export const TRUST_TYPE_NAME: Trust['__typename'] = 'Trust';
export const ENVELOPE_ACCOUNT_CASH_TYPE: CashAccount['__typename'] = 'CashAccount';
export const ENVELOPE_ACCOUNT_MANAGED_TYPE: ManagedAccount['__typename'] = 'ManagedAccount';
export const ENVELOPE_ACCOUNT_DIRECT_TRADING_TYPE: DirectTradingAccount['__typename'] = 'DirectTradingAccount';

export const SUPPORT_EMAIL = 'support@bettermentadvisorsolutions.com';

export const LEGAL_ACCOUNT_TYPE_DISPLAY_NAMES: Record<LegalAccountType, string> = {
  [LegalAccountType.IndividualTaxable]: 'Individual taxable',
  [LegalAccountType.JointTaxable]: 'Joint taxable',
  [LegalAccountType.Roth_401K]: 'Roth 401(k)',
  [LegalAccountType.RothIra]: 'Roth IRA',
  [LegalAccountType.SepIra]: 'SEP IRA',
  [LegalAccountType.Traditional_401K]: 'Traditional 401(k)',
  [LegalAccountType.TraditionalIra]: 'Traditional IRA',
  [LegalAccountType.TrustTaxable]: 'Trust taxable',
  [LegalAccountType.Hsa]: 'Health Savings Account',
  [LegalAccountType.Holding_529]: '529 Plan',
};
